/**
 * UTM & Click ID Manager
 * Captures, persists, and provides attribution data
 */

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface ClickIds {
  gclid?: string;     // Google Ads
  gbraid?: string;    // Google Ads (iOS)
  wbraid?: string;    // Google Ads (web-to-app)
  fbclid?: string;    // Meta/Facebook
  ttclid?: string;    // TikTok
  li_fat_id?: string; // LinkedIn
  msclkid?: string;   // Microsoft/Bing
}

export interface AttributionData extends UTMParams, ClickIds {
  referrer?: string;
  landingPage?: string;
  timestamp?: string;
}

const STORAGE_KEY = 'attribution_data';
const EXPIRY_DAYS = 30;

// Parse URL for UTM and click IDs
export const parseURLParams = (): AttributionData => {
  const params = new URLSearchParams(window.location.search);
  
  const data: AttributionData = {};
  
  // UTM parameters
  if (params.get('utm_source')) data.utm_source = params.get('utm_source')!;
  if (params.get('utm_medium')) data.utm_medium = params.get('utm_medium')!;
  if (params.get('utm_campaign')) data.utm_campaign = params.get('utm_campaign')!;
  if (params.get('utm_content')) data.utm_content = params.get('utm_content')!;
  if (params.get('utm_term')) data.utm_term = params.get('utm_term')!;
  
  // Click IDs
  if (params.get('gclid')) data.gclid = params.get('gclid')!;
  if (params.get('gbraid')) data.gbraid = params.get('gbraid')!;
  if (params.get('wbraid')) data.wbraid = params.get('wbraid')!;
  if (params.get('fbclid')) data.fbclid = params.get('fbclid')!;
  if (params.get('ttclid')) data.ttclid = params.get('ttclid')!;
  if (params.get('li_fat_id')) data.li_fat_id = params.get('li_fat_id')!;
  if (params.get('msclkid')) data.msclkid = params.get('msclkid')!;
  
  return data;
};

// Get stored attribution data
export const getStoredAttribution = (): AttributionData | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    
    // Check expiry
    if (parsed.expiry && new Date(parsed.expiry) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return parsed.data;
  } catch {
    return null;
  }
};

// Save attribution data
export const saveAttribution = (data: AttributionData): void => {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + EXPIRY_DAYS);
  
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      data,
      expiry: expiry.toISOString(),
    })
  );
  
  // Also set as cookies for server-side access
  Object.entries(data).forEach(([key, value]) => {
    if (value) {
      document.cookie = `_${key}=${encodeURIComponent(value)}; max-age=${EXPIRY_DAYS * 86400}; path=/; SameSite=Lax`;
    }
  });
};

// Initialize and capture UTMs on page load
export const initializeAttribution = (): AttributionData => {
  const urlParams = parseURLParams();
  const stored = getStoredAttribution();
  
  // Merge: new URL params override stored
  const hasNewParams = Object.keys(urlParams).length > 0;
  
  const merged: AttributionData = {
    ...stored,
    ...urlParams,
    referrer: document.referrer || stored?.referrer,
    landingPage: hasNewParams ? window.location.pathname : stored?.landingPage,
    timestamp: hasNewParams ? new Date().toISOString() : stored?.timestamp,
  };
  
  // Only save if we have attribution data
  if (Object.keys(merged).some(k => merged[k as keyof AttributionData])) {
    saveAttribution(merged);
  }
  
  return merged;
};

// Get current attribution (for forms/events)
export const getAttribution = (): AttributionData => {
  return getStoredAttribution() || initializeAttribution();
};

// Format attribution for form submission
export const getFormattedAttribution = (): Record<string, string> => {
  const data = getAttribution();
  const formatted: Record<string, string> = {};
  
  Object.entries(data).forEach(([key, value]) => {
    if (value) {
      formatted[key] = String(value);
    }
  });
  
  return formatted;
};

// Get attribution as hidden form fields
export const getAttributionFields = (): Array<{ name: string; value: string }> => {
  const data = getAttribution();
  return Object.entries(data)
    .filter(([, value]) => value)
    .map(([name, value]) => ({ name, value: String(value) }));
};

// Clear attribution (for testing)
export const clearAttribution = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};
